import { filter, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { getCountry } from '@cemex/cmx-client-layout';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { FieldProductSelectionConfig } from '../../../../../../shared/modules/configurable-form/models/field-configs/field-product-selection-config.interface';
import { getSelectedBusinessLine, getSpecificationForm, OrderBuilderState } from '../../../../../reducers/index';
import { AbstractField } from '../../../../../../shared/modules/configurable-form/models/field-configs/abstract-field';
import { CmxClientService, getLegalEntity } from '@cemex/cmx-client-layout';
import { ProductCatalogModal } from '../product-catalog/product-catalog.component';
import {
  LOAD_PRODUCT_FROM_API_FAIL,
  LOAD_PRODUCT_FROM_STORE_SUCCESS,
  SELECT_PRODUCT,
  SpecificationLoadProductFromStoreAction,
  SpecificationLoadProductsSelectionAction,
} from '../../../actions/specification.actions';
import { environment } from '../../../../../../../../../environments/environment';
import { IProduct } from '@cemex-core/cmx-api/models/product/models/product.interface';
import { SpecFieldNames } from '../../../constants/specification-field-names.enum';
import { TranslateService } from '@ngx-translate/core';
import { COUNTRY_CODE } from '@cemex-core/cmx-api';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from '@ordersnproducts/features';
import { getActiveExperienceCode } from '@ordersnproducts/modules/order-builder/reducers/index';
import { ORDER_EXPERIENCES } from '@ordersnproducts/modules/shared/constants/order-experiences';
import {
  PCMLoadProductAction,
  ProductsActionTypes,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/actions/open-platform/products.actions';
import { getOpenPlatformProducts } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/products.selectors';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { CmxSpinnerService } from '@ordersnproducts/modules/shared/components/cmx-spinner/cmx-spinner.service';
import { FormControlMessageError } from '@ordersnproducts/utils/formControlStatusMessage';
import {
  formatTopProductsMixReference,
  formatOpenPlatformTopProductsMixReference,
} from '@ordersnproducts/utils/mix-reference';
import { getLocalStorageItem } from '../../../../../../../utils/safe-storage';

@Component({
  selector: 'form-hybris-product-selection',
  styleUrls: ['form-hybris-product-selection.component.scss'],
  templateUrl: 'form-hybris-product-selection.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormHybrisProductSelectionComponent extends AbstractField implements OnInit, OnDestroy {
  config: FieldProductSelectionConfig;
  group: UntypedFormGroup;
  options$: Observable<
    {
      id: IProduct | string;
      name: string;
    }[]
  >;
  isRequired = false;
  fieldControl: UntypedFormControl;
  destroyed$ = new Subject<boolean>();
  searchingProducts$: BehaviorSubject<string> = new BehaviorSubject('');
  showButton = false;
  orderExperience: string;
  originalProductForm;
  countryCode: string;
  tempSelectedProduct: string;
  selectedValue: string;

  constructor(
    public dialog: MatDialog,
    public store: Store<OrderBuilderState>,
    private updates$: Actions,
    private alerts: CmxClientService,
    public formControlMessageError: FormControlMessageError,
    private ref: ChangeDetectorRef,
    private translate: TranslateService,
    private featureToggleService: FeatureToggleService,
    private spinner: CmxSpinnerService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.spinner.show();
    this.preloadProducts();
    this.showButton = this.featureToggleService.isFeatureEnabled(Features.CatalogPCM) && !this.config.hideButton;
    this.fieldControl = this.getControl() as UntypedFormControl;
    this.isRequired = this.hasRequiredField(this.fieldControl);

    const monitor$ = this.updates$.pipe(
      ofType(ProductsActionTypes.PCM_SELECT_PRODUCT, ProductsActionTypes.PCM_LOAD_PRODUCT_SUCCESS),
      map((action: any) => {
        const product = action.payload;
        this.ref.markForCheck();
        // if (this.group.controls[SpecFieldNames.CONTRACT_SELECT]) {
        // this.group.controls[SpecFieldNames.CONTRACT_SELECT].patchValue(undefined);
        // }
        this.group.controls[SpecFieldNames.PRODUCT_SELECTION].patchValue(
          _safe(() => product.productCode.replace(/^0+/, '')) || _safe(() => product.commercialCode.replace(/^0+/, '')),
        );
      }),
      takeUntil(this.destroyed$),
    );
    monitor$.subscribe();
    this.updates$
      .pipe(ofType(SELECT_PRODUCT, LOAD_PRODUCT_FROM_STORE_SUCCESS), takeUntil(this.destroyed$))
      .subscribe((action) => {
        this.ref.markForCheck();
        if (this.group.controls[SpecFieldNames.CONTRACT_INFORMATIVE]) {
          this.group.controls[SpecFieldNames.CONTRACT_INFORMATIVE].patchValue(undefined);
        }
        this.group.controls[SpecFieldNames.PRODUCT_SELECTION].patchValue(action['payload']);
      });

    this.updates$.pipe(ofType(LOAD_PRODUCT_FROM_API_FAIL), takeUntil(this.destroyed$)).subscribe(() => {
      this.ref.markForCheck();
      this.alerts.closeAllAlerts();
      this.alerts.addAlert('main', 'danger', this.translate.instant('views.common.ping_unsuccessful'));
    });
    // this.originalProductForm = '';
    // this.group.controls[SpecFieldNames.PRODUCT_SELECTION].setValue(undefined);
  }

  protected preloadProducts() {
    this.store
      .select(getActiveExperienceCode)
      .pipe(
        map((experience) => (this.orderExperience = experience)),
        take(1),
      )
      .subscribe();

    if (this.config.preloadTopProducts) {
      this.store.pipe(select(getCountry)).subscribe((country) => {
        this.countryCode = country;
        if (
          this.orderExperience === ORDER_EXPERIENCES.SIMMA_UNIVERSAL_LINK ||
          this.countryCode === COUNTRY_CODE.ISRAEL
        ) {
          this.options$ = this.preloadOpenPlatformTopProducts();
        } else {
          this.options$ = this.preloadTopProducts();
        }
        if (this.config.dropdownSalesDocumentTypes) {
          this.store.dispatch(new SpecificationLoadProductsSelectionAction(this.config.dropdownSalesDocumentTypes));
        } else {
          this.spinner.hide();
          throw new Error(
            'FormProductSelectionComponent: Property "dropdownSalesDocumentTypes" of config' +
              ' needs to be set if "preloadTopProducts" enabled',
          );
        }
      });
    }
  }

  protected preloadTopProducts() {
    return this.store.select(getSpecificationForm).pipe(
      withLatestFrom(this.store.select(getSelectedBusinessLine)),
      map(([state, selectedBusinesLine]) => {
        this.spinner.hide();
        return state.productSelectionProducts.map((p) => {
          return {
            id: p,
            name: formatTopProductsMixReference(p, selectedBusinesLine.productTypeCode),
          };
        });
      }),
    );
  }

  protected preloadOpenPlatformTopProducts() {
    return this.store.pipe(
      select(getOpenPlatformProducts),
      filter((availableProducts) => availableProducts.length > 0),
      withLatestFrom(this.store.select(getSelectedBusinessLine)),
      map(([availableTopProducts, selectedBusinessLine]) => {
        this.spinner.hide();
        const zerosFormat = '0000000000';
        const prodWithFormat = zerosFormat.concat(this.originalProductForm);
        return availableTopProducts
          .filter(
            (topProduct) =>
              topProduct['isTopProduct'] === true ||
              topProduct.productCode === prodWithFormat ||
              topProduct.productCode.includes(this.tempSelectedProduct),
          )
          .map((product) => {
            return {
              id: product.productCode.replace(/^0+/, ''),
              name: formatOpenPlatformTopProductsMixReference(product, selectedBusinessLine.productTypeCode),
            };
          });
      }),
    );
  }

  openDialog(): void {
    this.store
      .select(getLegalEntity)
      .pipe(take(1))
      .subscribe((customer) => {
        const countryCode = customer.countryCode;
        const pcmUrl = this.getPcmUrl(countryCode);

        const dialogRef = this.dialog.open(ProductCatalogModal, {
          data: {
            pcmUrl,
            countryCode,
          },
          height: '90vh',
          width: '90vw',
          maxWidth: '1300px',
          maxHeight: '750px',
          panelClass: 'hybris-dialog-container',
        });

        dialogRef
          .afterClosed()
          .pipe(filter((val) => val !== null))
          .subscribe((value) => {
            this.tempSelectedProduct = value.selectedProducts[0].code.replace(/^0+/, '');
            this.ref.markForCheck();
            this.setPcmProducts(value);
          });
      });
  }

  getCurrentLanguage(): string {
    return getLocalStorageItem('language') || 'en_US';
  }

  getPcmUrl(countryCode: COUNTRY_CODE): string {
    return `${environment.pcmUrl}/${
      environment.pcmRegionNames[countryCode]
    }/catalog?cxlang=${this.getCurrentLanguage()}`;
  }

  setPcmProducts(response): void {
    this.spinner.show();
    if (response.selectedProducts) {
      const selectProduct$ = this.store.select(getActiveExperienceCode).pipe(
        map((experience) => {
          response.selectedProducts.map((pcmProduct) => {
            if (experience === ORDER_EXPERIENCES.SIMMA_UNIVERSAL_LINK) {
              this.store.dispatch(new PCMLoadProductAction({ productCode: String(pcmProduct.code) }));
            } else {
              this.store.dispatch(
                new SpecificationLoadProductFromStoreAction({ productCode: String(pcmProduct.code) }),
              );
            }
            this.listFilteredByTerm(pcmProduct.code.replace(/^0+/, ''));
          });
        }),
        take(1),
      );
      selectProduct$.subscribe();
    }
  }

  onSearch(event: CustomEvent<string>) {
    if (!event) {
      return;
    }
    this.options$ = this.listFilteredByTerm(event.detail);
  }

  protected listFilteredByTerm(name: string) {
    if (name) {
      return this.store.pipe(
        select(getOpenPlatformProducts),
        filter((availableProducts) => availableProducts.length > 0),
        withLatestFrom(this.store.select(getSelectedBusinessLine)),
        map(([availableTopProducts, selectedBusinessLine]) => {
          this.spinner.hide();
          return availableTopProducts
            .map((product) => {
              return {
                id: product.productCode.replace(/^0+/, ''),
                name: formatOpenPlatformTopProductsMixReference(product, selectedBusinessLine.productTypeCode),
              };
            })
            .filter((nameFiltered) => nameFiltered.name.toLowerCase().includes(name.toLowerCase()));
        }),
      );
    } else {
      this.spinner.hide();
      this.preloadProducts();
      return this.options$;
    }
  }

  waitUntilLoadData(event: CustomEvent<string>) {
    if (event.detail) {
      this.tempSelectedProduct = event.detail;
      this.preloadProducts();
    }
  }

  ngOnDestroy(): void {
    this.originalProductForm = '';
    this.group.controls[SpecFieldNames.PRODUCT_SELECTION].setValue(undefined);
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
